import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2, BellRing, Send } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

export function OpsAlerts({ eventId }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/events/${eventId}/ops-alerts`).then((r) => setCfg(r.data))
    .catch((e) => toast.error(errText(e)));
  useEffect(() => { setCfg(null); load(); }, [eventId]); // eslint-disable-line

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/events/${eventId}/ops-alerts`, {
        enabled: cfg.enabled,
        phones: (cfg.phones || []).filter((p) => p.trim()),
        emails: (cfg.emails || []).filter((e) => e.trim()),
        registrant_confirmation: cfg.registrant_confirmation,
        registrant_payment_failed: cfg.registrant_payment_failed,
        organiser_copy: cfg.organiser_copy,
      });
      setCfg({ ...cfg, ...data });
      toast.success(data.enabled ? "Alerts on — you'll get a WhatsApp and email on every failure" : "Alerts turned off");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/events/${eventId}/ops-alerts/test`);
      const mailed = (data.emails || []).filter((e) => e.email_id).length;
      const queued = (data.whatsapp || []).filter((w) => w.message_id).length;
      toast.success(`Test alert: ${queued} WhatsApp queued · ${mailed} email sent`);
      if (data.email_error) toast.error(`Email: ${data.email_error}`);
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  if (!cfg) return <div className="mono-label" data-testid="ops-alerts-loading">Loading ops alerts…</div>;

  const list = (key, placeholder, testid) => (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {(cfg[key] || []).map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input data-testid={`${testid}-${i}`} className="input-x !py-2" placeholder={placeholder} value={v}
            onChange={(e) => setCfg({ ...cfg, [key]: cfg[key].map((x, idx) => idx === i ? e.target.value : x) })} />
          <button data-testid={`${testid}-del-${i}`} className="p-2 hover:text-[var(--red)]"
            onClick={() => setCfg({ ...cfg, [key]: cfg[key].filter((_, idx) => idx !== i) })}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );

  return (
    <Panel className="p-6 mt-6" data-testid="ops-alerts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonoLabel><BellRing size={13} className="inline mr-2" />Instant failure alerts</MonoLabel>
        <button data-testid="ops-toggle" onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
          className={cfg.enabled ? "btn-red" : "btn-ghost"}>
          {cfg.enabled ? "Alerts: ON" : "Alerts: OFF"}
        </button>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <MonoLabel>WhatsApp numbers</MonoLabel>
        <button data-testid="ops-add-phone" className="btn-ghost"
          onClick={() => setCfg({ ...cfg, phones: [...(cfg.phones || []), ""] })}><Plus size={14} /> Add</button>
      </div>
      {list("phones", "10-digit mobile", "ops-phone")}

      <div className="mt-6 flex items-center justify-between">
        <MonoLabel>Backup email addresses</MonoLabel>
        <button data-testid="ops-add-email" className="btn-ghost"
          onClick={() => setCfg({ ...cfg, emails: [...(cfg.emails || []), ""] })}><Plus size={14} /> Add</button>
      </div>
      {list("emails", "you@example.com", "ops-email")}

      <div className="mt-6 hairline-t pt-5">
        <MonoLabel>Member emails</MonoLabel>
        <div className="mt-3 space-y-3">
          {[
            ["registrant_confirmation", "Registration confirmed + GST invoice link to the member"],
            ["registrant_payment_failed", "Payment failed → member gets a resume-payment email"],
            ["organiser_copy", "Copy every confirmation to the organiser emails above"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-start gap-3 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`ops-mail-${key}`} className="mt-1" checked={!!cfg[key]}
                onChange={(e) => setCfg({ ...cfg, [key]: e.target.checked })} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button data-testid="ops-save" className="btn-red" disabled={!!busy} onClick={save}>
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button data-testid="ops-test" className="btn-ghost" disabled={!!busy} onClick={sendTest}>
          {busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test alert
        </button>
      </div>

      {(cfg.recent || []).length > 0 && (
        <div className="mt-6 hairline-t pt-4">
          <MonoLabel>Last alerts</MonoLabel>
          <div className="mt-3 space-y-2">
            {cfg.recent.map((a, i) => (
              <div key={a.id} className="text-sm" data-testid={`ops-recent-${i}`}>
                <span className="mono-label" style={{ color: "var(--red-2)" }}>{a.alert_type}</span>
                <span className="ml-2">{a.who}</span>
                <span className="mono-label ml-2" style={{ color: "var(--taupe)" }}>{a.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mono-label mt-5" style={{ color: "var(--muted)" }}>
        Fires instantly on every failed payment and every failed WhatsApp send, for all registrations.
        Repeats of the same failure are muted for 6 hours. WhatsApp uses the <span className="font-mono">ops_alert</span> template
        (AiSensy campaign <span className="font-mono">bni_md_ops_alert</span>) — it must be approved in AiSensy for WhatsApp to deliver; email works regardless.
      </div>
    </Panel>
  );
}
